import { useEffect, useState } from 'react';
import {
  FormAutocompleteV2,
  FormModalSelectV2,
  FormTextInputV2,
} from '@wajeez-web/components/form-builder';
import {
  createContentEntityTypeNavigation,
  editContentEntityTypeNavigation,
  getCollectionComponentsStandAloneLookups,
  getComponentComponentsLookups,
} from '@wajeez-web/stores/content';
import {
  useCollectionComponentsStandAloneLookups,
  useComponentComponentsLookups,
  useContentEntityTypesLookups,
  useCreateContentEntityTypeNavigation,
  useEditContentEntityTypeNavigation,
} from '@wajeez-web/components/helpers';
import { SubmitHandler, useForm } from 'react-hook-form';
import { BiLoaderAlt } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { Server } from '@wajeez-web/core/api-base';
import { ButtonHeadless } from '@wajeez-web/components/ui';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';

type Input = Server.CreateContentEntityTypeNavigationInput;

interface ContentEntityTypeNavigationFormBuilderProps {
  id?: string;
  data?: Server.DashboardContentEntityTypeNavigationOutput | null;
}

export const ContentEntityTypeNavigationFormBuilder = ({
  id,
  data,
}: ContentEntityTypeNavigationFormBuilderProps) => {
  const navigate = useNavigate();

  const [
    componentComponentsLookupsDefaultValue,
    setComponentComponentsLookupsDefaultValue,
  ] = useState<Server.ComponentsLookUpOutput[]>();
  const [
    collectionComponentsStandAloneLookupsDefaultValue,
    setCollectionComponentsStandAloneLookupsDefaultValue,
  ] = useState<Server.CollectionComponentLookUpOutput[]>();
  const [
    isComponentComponentsLookupsFirstRenderDone,
    setIsComponentComponentsLookupsFirstRenderDone,
  ] = useState(false);
  const [
    isCollectionComponentsStandAloneLookupsFirstRenderDone,
    setIsCollectionComponentsStandAloneLookupsFirstRenderDone,
  ] = useState(false);

  const create = useCreateContentEntityTypeNavigation();
  const edit = useEditContentEntityTypeNavigation();

  const contentEntityTypesLookups = useContentEntityTypesLookups();

  const componentComponentsLookups = useComponentComponentsLookups();

  const collectionComponentsStandAloneLookups =
    useCollectionComponentsStandAloneLookups();

  const { handleSubmit, control } = useForm<Input>({
    defaultValues: {
      navigationName: data?.navigationName,
      detailsPageId: data?.detailsPageId,
      playerPageId: data?.playerPageId,
      searchPageId: data?.searchPageId,
      searchComponentId: data?.searchComponentId,
    },
  });

  const onSubmitSuccess = () => {
    navigate(`/type-navigations`);
  };

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    const mappedData: Input = {
      ...data,
      contentEntityTypeId: data.contentEntityTypeId
        ? Number(data.contentEntityTypeId)
        : undefined,
    };

    if (!id) {
      createContentEntityTypeNavigation(mappedData, onSubmitSuccess);
    } else {
      editContentEntityTypeNavigation(
        { ...mappedData, id: Number(id) },
        onSubmitSuccess
      );
    }
  };

  const onCancel = () => {
    navigate(`/type-navigations`);
  };

  useEffect(() => {
    if (
      componentComponentsLookups.data &&
      !isComponentComponentsLookupsFirstRenderDone
    ) {
      setComponentComponentsLookupsDefaultValue(
        componentComponentsLookups.data
      );

      setIsComponentComponentsLookupsFirstRenderDone(true);
    }

    if (
      collectionComponentsStandAloneLookups.data &&
      !isCollectionComponentsStandAloneLookupsFirstRenderDone
    ) {
      setCollectionComponentsStandAloneLookupsDefaultValue(
        collectionComponentsStandAloneLookups.data
      );

      setIsCollectionComponentsStandAloneLookupsFirstRenderDone(true);
    }
  }, [
    isComponentComponentsLookupsFirstRenderDone,
    isCollectionComponentsStandAloneLookupsFirstRenderDone,
    componentComponentsLookups.data,
    collectionComponentsStandAloneLookups.data,
  ]);

  return (
    <div>
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        noValidate
        className="w-full flex flex-col justify-start items-start"
      >
        <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
          <FormTextInputV2
            name="navigationName"
            control={control}
            fieldMeta={{
              fieldType: Server.FieldType.Text,
              fieldOptions: {},
              label: 'Navigation Name',
              placeHolder: 'Navigation Name',
              required: true,
            }}
            required
          />
          {!id && (
            <div className="w-full">
              <FormAutocompleteV2
                name="contentEntityTypeId"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {
                    selectOptions: contentEntityTypesLookups.data.map((c) => {
                      return {
                        value: c.id?.toString(),
                        label: c.displayNamePlural + ` (${c.name})`,
                      };
                    }),
                  },
                  label: 'Content Entity Type',
                  placeHolder: 'Content Entity Type',
                }}
              />
            </div>
          )}
          <div className="mt-3 w-full">
            <FormModalSelectV2
              name="detailsPageId"
              control={control}
              fieldMeta={{
                fieldType: Server.FieldType.Select,
                fieldOptions: {
                  defaultOptions:
                    collectionComponentsStandAloneLookupsDefaultValue?.map(
                      (p) => {
                        return {
                          value: p.id ?? '',
                          label: p.referenceName,
                        };
                      }
                    ),
                  selectOptions: collectionComponentsStandAloneLookups.data.map(
                    (p) => {
                      return {
                        value: p.id ?? '',
                        label: p.referenceName,
                      };
                    }
                  ),
                },
                label: 'Details Page',
              }}
              onRender={() => {
                getCollectionComponentsStandAloneLookups({});
              }}
              onSearch={(searchText) => {
                getCollectionComponentsStandAloneLookups({ searchText });
              }}
            />
          </div>
          <div className="mt-3 w-full">
            <FormModalSelectV2
              name="playerPageId"
              control={control}
              fieldMeta={{
                fieldType: Server.FieldType.Select,
                fieldOptions: {
                  defaultOptions:
                    collectionComponentsStandAloneLookupsDefaultValue?.map(
                      (p) => {
                        return {
                          value: p.id ?? '',
                          label: p.referenceName,
                        };
                      }
                    ),
                  selectOptions: collectionComponentsStandAloneLookups.data.map(
                    (p) => {
                      return {
                        value: p.id ?? '',
                        label: p.referenceName,
                      };
                    }
                  ),
                },
                label: 'Player Page',
              }}
              onRender={() => {
                getCollectionComponentsStandAloneLookups({});
              }}
              onSearch={(searchText) => {
                getCollectionComponentsStandAloneLookups({ searchText });
              }}
            />
          </div>
          <div className="mt-3 w-full">
            <FormModalSelectV2
              name="searchPageId"
              control={control}
              fieldMeta={{
                fieldType: Server.FieldType.Select,
                fieldOptions: {
                  defaultOptions:
                    collectionComponentsStandAloneLookupsDefaultValue?.map(
                      (p) => {
                        return {
                          value: p.id ?? '',
                          label: p.referenceName,
                        };
                      }
                    ),
                  selectOptions: collectionComponentsStandAloneLookups.data.map(
                    (p) => {
                      return {
                        value: p.id ?? '',
                        label: p.referenceName,
                      };
                    }
                  ),
                },
                label: 'Search Page',
              }}
              onRender={() => {
                getCollectionComponentsStandAloneLookups({});
              }}
              onSearch={(searchText) => {
                getCollectionComponentsStandAloneLookups({ searchText });
              }}
            />
          </div>
          <div className="mt-3 w-full">
            <FormModalSelectV2
              name="searchComponentId"
              control={control}
              fieldMeta={{
                fieldType: Server.FieldType.Select,
                fieldOptions: {
                  defaultOptions: componentComponentsLookupsDefaultValue?.map(
                    (p) => {
                      return {
                        value: p.id ?? '',
                        label: p.referenceName,
                      };
                    }
                  ),
                  selectOptions: componentComponentsLookups.data.map((p) => {
                    return {
                      value: p.id ?? '',
                      label: p.referenceName,
                    };
                  }),
                },
                label: 'Search Component',
              }}
              onRender={() => {
                getComponentComponentsLookups({});
              }}
              onSearch={(searchText) => {
                getComponentComponentsLookups({ searchText });
              }}
            />
          </div>
        </div>
        <div className="mt-6 w-full flex justify-end items-center space-s-2">
          <div className="w-[70px]">
            <ButtonHeadless
              bgStyle="bg-warmGray-300"
              borderStyle="border-none"
              paddingYStyle="py-1"
              action={onCancel}
            >
              <div className="text-warmGray-600 text-[16px] font-medium">
                <ReturnTranslatedText
                  text="buttons.Cancel"
                  origintext="Cancel"
                />
              </div>
            </ButtonHeadless>
          </div>
          <div className="w-[70px]">
            <ButtonHeadless
              type="submit"
              borderStyle="border-none"
              paddingYStyle="py-1"
            >
              <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                <ReturnTranslatedText text="buttons.Save" origintext="Save" />
                {(create.status === 'loading' || edit.status === 'loading') && (
                  <BiLoaderAlt className="animate-spin w-5" />
                )}
              </div>
            </ButtonHeadless>
          </div>
        </div>
      </form>
    </div>
  );
};
